import { useCallback, useState } from "react";
import { FaroMenuItem } from "@faro-lotv/flat-ui";
import { onCancelImport } from "@pages/data-management/import-data/cancel-import-utils";
import { assert } from "@faro-lotv/foundation";
import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { useRegistrationApiClient } from "@api/registration-api/use-registration-api-client";
import { useCancelRevision } from "@hooks/data-management/use-cancel-revision";
import { useDialog } from "@components/common/dialog/dialog-provider";
import { uploadTasksSelector } from "@store/upload-tasks/upload-tasks-selector";
import { useFileUploadContext } from "@context-providers/file-upload/file-uploads-context";
import { useAppSelector, useAppDispatch } from "@store/store-helper";
import { selectedProjectSelector } from "@store/projects/projects-selector";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { currentUserSelector } from "@store/user/user-selector";
import { isRegisteringSelector } from "@pages/data-management/registered-data/registered-data-selectors";
import { useToast } from "@hooks/use-toast";
import { useProgressApiClient } from "@api/progress-api/use-progress-api-client";
import { WorkflowState } from "@pages/data-management/data-management-types";
import { SphereTooltip } from "@components/common/sphere-tooltip";

/** Props for RevertUnpublishedChangesMenuItem */
interface Props {
  /** Workflow state of the project */
  state: WorkflowState;
  /** Callback to set whether to prevent the redirect to the Viewer when the published state is reached. */
  setShouldPreventRedirect: React.Dispatch<React.SetStateAction<boolean>>;
  /** Callback function to be executed on click */
  onClickCallback?: () => void;
  /** Boolean to detect if dropzone loader is active */
  isLoading? : boolean;
  /** Callback to set the dropzone loader while fetching data from ProjectApi */
  setIsLoading? : React.Dispatch<React.SetStateAction<boolean>>;
  /** Callback to show/hide the spinner in dropzone/table view when revert unpublished scans btn is clicked */
  setIsRevertBtnClicked? : React.Dispatch<React.SetStateAction<boolean>>;
}

export function RevertUnpublishedChangesMenuItem({
  state,
  setShouldPreventRedirect,
  onClickCallback = () => {/** NOP */ },
  isLoading,
  setIsLoading,
  setIsRevertBtnClicked,
}: Props): JSX.Element {

  const dispatch = useAppDispatch();
  const { createDialog } = useDialog();
  const uploadTasks = useAppSelector(uploadTasksSelector);
  const { uploadManager } = useFileUploadContext();
  const cancelRevision = useCancelRevision();
  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorContext();
  const { showToast } = useToast();

  const project = useAppSelector(selectedProjectSelector);
  assert(project, "Project has to be defined");
  const currentUser = useAppSelector(currentUserSelector);
  assert(currentUser, "User has to be defined");
  const projectApiClient = useProjectApiClient({ projectId: project.id });
  const progressApiClient = useProgressApiClient({ projectId: project.id.toString() });
  const registrationApiClient = useRegistrationApiClient({ projectId: project.id });

  const isRegistering = useAppSelector(isRegisteringSelector);

  /** Flag to prevent multiple cancel requests. */
  const [cancelInProgress, setCancelInProgress] = useState(false);

  /**
   * Asks for confirmation, and discards the current draft revision.
   */
  const onCancelDraft = useCallback(async (): Promise<void> => {
    onClickCallback();
    await onCancelImport(
      dispatch,
      projectApiClient,
      progressApiClient,
      registrationApiClient,
      showToast,
      handleErrorWithToast,
      true /* shouldUseDiscard */,
      createDialog,
      setCancelInProgress,
      state,
      uploadTasks,
      uploadManager,
      cancelRevision,
      trackEvent,
      isRegistering,
      setShouldPreventRedirect,
      isLoading,
      setIsLoading,
      setIsRevertBtnClicked
    );
  }, [
    cancelRevision,
    createDialog,
    dispatch,
    handleErrorWithToast,
    isRegistering,
    onClickCallback,
    progressApiClient,
    projectApiClient,
    registrationApiClient,
    setShouldPreventRedirect,
    showToast,
    state,
    trackEvent,
    uploadManager,
    uploadTasks,
    isLoading,
    setIsLoading,
    setIsRevertBtnClicked,
  ]);

  return (
    <SphereTooltip
      title={cancelInProgress ?
        "The project's draft version has already been cancelled. Please wait." :
        "Discard the project's draft version."}
    >
      <FaroMenuItem
        label="Revert unpublished changes"
        onClick={() => { void onCancelDraft(); }}
        disabled={cancelInProgress}
      />
    </SphereTooltip>
  );
}
